import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import i18n from '../../i18n';

export function handleControlsPdfDownload(
    fetchData,
    setShowControlsDropdown,
    contentMap = {},
    frameworkData = null,
    ownerEmailParam = '',
    customFileName = 'Controls_List.pdf',
    scopeParam = '',
    userData = null
) {
    const controlData = [];
    let tocPageNumber = 0;
    let sectionOrder = Object.keys(fetchData?.categories || {});
    if (sectionOrder.length === 0) {
        sectionOrder = ['SMART MAP CONTROLS'];
    }

    const sectionConfig = {};
    const colors = [
        [246, 244, 150], [58, 166, 229], [100, 45, 134],
        [242, 198, 0], [209, 0, 31], [0, 150, 102]
    ];
    const texts = [
        [0, 0, 0], [255, 255, 255], [255, 255, 255],
        [0, 0, 0], [255, 255, 255], [255, 255, 255]
    ];
    const alts = [
        [250, 249, 237], [235, 248, 255], [242, 235, 247],
        [255, 248, 204], [252, 229, 229], [219, 244, 233]
    ];

    sectionOrder.forEach((sec, idx) => {
        const cIdx = idx % colors.length;
        sectionConfig[sec] = {
            title: sec,
            headerLabel: sec,
            firstColumnFill: colors[cIdx],
            firstColumnText: texts[cIdx],
            priorityFill: [224, 224, 224],
            priorityText: [0, 0, 0],
            alternateFill: alts[cIdx],
        };
    });

    const sections = sectionOrder.reduce((acc, key) => {
        acc[key] = [];
        return acc;
    }, {});

    const headingLookup = {};

    const STATUS_ABBREVIATIONS = {
        'partially implemented': 'PI',
        'not implemented': 'NI',
        'fully implemented': 'FI',
        'largely implemented': 'LI',
        'in practice': 'IP',
    };

    const formatStatusValue = (value) => {
        const raw = typeof value === 'string' ? value.trim() : value?.toString?.().trim?.() || '';
        if (!raw) return '';
        const abbreviated = STATUS_ABBREVIATIONS[raw.toLowerCase()];
        return abbreviated || raw;
    };

    const buildHeadingKey = (sectionKey, categoryCode) => {
        if (!sectionKey || !categoryCode) return null;
        const cleanedCode = String(categoryCode).replace(/:$/, '').trim();
        if (!cleanedCode) return null;
        return `${sectionKey}_${cleanedCode}`;
    };

    const storeHeading = (sectionKey, categoryCode, headingText) => {
        const key = buildHeadingKey(sectionKey, categoryCode);
        if (!key) return;
        const normalizedHeading = (headingText || '').toString().trim();
        if (!normalizedHeading) return;
        if (!headingLookup[key]) {
            headingLookup[key] = normalizedHeading;
        }
    };

    const resolveSectionKey = (categoryName = '') => {
        return categoryName || 'SMART MAP CONTROLS';
    };

    const deriveCategoryCode = (subcat = {}, rows = []) => {
        const directCode = subcat?.categoryCode || subcat?.category_code;
        if (directCode) return String(directCode).replace(/:$/, '');

        const question = Array.isArray(subcat?.questions) && subcat.questions.length ? subcat.questions[0] : null;
        const candidateId =
            question?.category_id ||
            question?.categoryid ||
            rows?.[0]?.categoryId ||
            '';
        if (!candidateId) return '';

        const cleaned = String(candidateId).replace(/:$/, '');
        const dashIndex = cleaned.indexOf('-');
        return dashIndex > 0 ? cleaned.slice(0, dashIndex) : cleaned;
    };

    if (frameworkData && typeof frameworkData === 'object') {
        Object.entries(frameworkData).forEach(([rawSectionKey, sectionValue]) => {
            const sectionKey = resolveSectionKey(rawSectionKey) || resolveSectionKey(sectionValue?.category) || rawSectionKey;

            const items = Array.isArray(sectionValue?.items)
                ? sectionValue.items
                : Array.isArray(sectionValue?.subcategories)
                    ? sectionValue.subcategories
                    : Array.isArray(sectionValue)
                        ? sectionValue
                        : [];

            items.forEach((item) => {
                const categoryCode = deriveCategoryCode(item, item?.questions);
                const headingText = item?.heading || item?.content || '';
                storeHeading(sectionKey, categoryCode, headingText);
            });
        });
    }

    const GOVERN_SUBCATEGORY_ORDER = [
        'Organizational Context',
        'Risk Management Strategy',
        'Roles, Responsibilities, and Authorities',
        'Policy',
        'Oversight',
        'Cybersecurity Supply Chain Risk Management',
    ];

    const SUBCATEGORY_HEADING_FALLBACKS = {
        'identity management, authentication, and access control':
            'Identity Management, Authentication, and Access Control (PR.AA): Access to physical and logical assets is limited to authorized users, services, and hardware and managed commensurate with the assessed risk of unauthorized access',
        'technology infrastructure resilience':
            "Technology Infrastructure Resilience (PR.IR): Security architectures are managed with the organization's risk strategy to protect asset confidentiality, integrity, and availability, and organizational resilience",
        'incident recovery plan execution':
            'Incident Recovery Plan Execution (RC.RP): Restoration activities are performed to ensure operational availability of systems and services affected by cybersecurity incidents',
        'incident recovery communication':
            'Incident Recovery Communication (RC.CO): Restoration activities are coordinated with internal and external parties',
    };

    Object.entries(fetchData?.categories || {}).forEach(([categoryName, categoryObj]) => {
        const sectionKey = resolveSectionKey(categoryName);

        const rawSubcategories = Array.isArray(categoryObj?.subcategories)
            ? [...categoryObj.subcategories]
            : [];

        const subcategories =
            sectionKey === 'GOVERN'
                ? rawSubcategories.sort((a, b) => {
                    const nameA = a?.subcategory_name || a?.subcategory || a?.task || '';
                    const nameB = b?.subcategory_name || b?.subcategory || b?.task || '';
                    const indexA = GOVERN_SUBCATEGORY_ORDER.indexOf(nameA);
                    const indexB = GOVERN_SUBCATEGORY_ORDER.indexOf(nameB);

                    if (indexA === -1 && indexB === -1) return nameA.localeCompare(nameB);
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    return indexA - indexB;
                })
                : rawSubcategories;

        subcategories.forEach((subcat) => {
            const rows = [];
            let categoryLabelFallback = subcat?.subcategory_name || subcat?.subcategory || '';

            subcat?.questions?.forEach((q) => {
                const categoryId = q.category_id || q.categoryid || '';
                const questionText = q.question || '';
                const priority = q.priority || 'N/A';
                const status = q.status || q.result || 'N/A';
                const remark = q.notes || q.remark || '';
                const categoryLabel = q.task || categoryLabelFallback || '';

                if (!categoryLabelFallback && categoryLabel) {
                    categoryLabelFallback = categoryLabel;
                }

                controlData.push({
                    "Category ID": categoryId,
                    "Category": categoryLabel,
                    "Sub Category": questionText,
                    "Priority": priority,
                    "Result": status,
                    "Remark": remark,
                });

                rows.push({
                    categoryId,
                    question: questionText,
                    priority,
                    status,
                    remark,
                });
            });

            if (sectionKey && sections[sectionKey] && rows.length) {
                const subcategoryName = subcat?.subcategory_name || subcat?.subcategory || categoryLabelFallback || 'Category';
                const description = subcat?.subcategory_description || subcat?.description || subcat?.details || '';
                const categoryCode = deriveCategoryCode(subcat, rows);
                const headingKey = buildHeadingKey(sectionKey, categoryCode);
                const fallbackHeading = SUBCATEGORY_HEADING_FALLBACKS[subcategoryName?.toLowerCase?.() || ''];
                const heading =
                    subcat?.heading ||
                    subcat?.content ||
                    (headingKey ? headingLookup[headingKey] : '') ||
                    fallbackHeading ||
                    '';

                storeHeading(sectionKey, categoryCode, heading || fallbackHeading);

                sections[sectionKey].push({
                    name: subcategoryName,
                    description,
                    heading,
                    categoryCode,
                    rows,
                    source: {
                        categoryName,
                        subcategory: subcat,
                    },
                });
            }
        });
    });

    if (controlData.length > 0) {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        // --- Cover Page (Page 1) ---
        // Add Logo
        try {
            const coverLogo = new Image();
            coverLogo.src = '/img/CARA_DarkTheme.png';
            const logoWidth = 70; 
            const logoHeight = 25;
            const pageWidth = doc.internal.pageSize.getWidth();
            const xPos = (pageWidth - logoWidth) / 2;
            doc.addImage(coverLogo, 'PNG', xPos, 40, logoWidth, logoHeight);
        } catch (e) {
            console.warn("Logo not found", e);
            doc.setFontSize(28);
            doc.setTextColor(27, 169, 218); // CARA Blue
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.text("CARA", pageWidth / 2, 60, { align: 'center' });
        }

        // Cover Table Data
        const reportVersion = "V1.0";
        const dateStr = new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });

        const ownerEmail = userData?.email || ownerEmailParam?.trim?.() || 'Not Available';
        const scopeText = scopeParam?.trim() || fetchData?.scope || fetchData?.assessment?.scope || 'Not Specified';

        const coverBody = [
            ["0", "Assessment Name", scopeText],
            ["1", "Report Version", reportVersion],
            ["2", "Prepared by", "CARAgrc"],
            ["3", "Generated by", (userData?.username && userData?.username.length > 50) ? (localStorage.getItem("username") || userData?.username) : (userData?.username || "User")],
            ["4", "Submitted by", (() => {
                const isEncrypted = (str) => str && str.startsWith("gAAAAA");
                const first = !isEncrypted(userData?.first_name) ? (userData?.first_name || localStorage.getItem("first_name") || "") : (localStorage.getItem("first_name") || "");
                const last = !isEncrypted(userData?.last_name) ? (userData?.last_name || localStorage.getItem("last_name") || "") : (localStorage.getItem("last_name") || "");
                const fullName = `${first} ${last}`.trim();
                return fullName || localStorage.getItem("username") || "User";
            })()],
            ["5", "Email", ownerEmail],
            ["6", "Date", dateStr]
        ];

        autoTable(doc, {
            startY: 100,
            head: [['Sr', 'Report Title', 'Smart Map Controls']],
            body: coverBody,
            theme: 'grid',
            styles: {
                fontSize: 10,
                cellPadding: 4,
                valign: 'middle',
                lineColor: [0, 0, 0], // Black border
                lineWidth: 0.1,
            },
            headStyles: {
                fillColor: [27, 169, 218], // CARA Blue
                textColor: [255, 255, 255], // White text
                fontStyle: 'bold',
                halign: 'left'
            },
            columnStyles: {
                0: { cellWidth: 20, fillColor: [27, 169, 218], textColor: [255, 255, 255], fontStyle: 'bold' }, // Sr col
                1: { cellWidth: 60, fillColor: [27, 169, 218], textColor: [255, 255, 255], fontStyle: 'bold' }, // Report Title col
                2: { cellWidth: 'auto', textColor: [0, 0, 0] } // Value col (White bg by default)
            },
            didParseCell: (data) => {
                // For body rows, ensure first two columns have Blue bg
                if (data.section === 'body') {
                    if (data.column.index === 0 || data.column.index === 1) {
                        data.cell.styles.fillColor = [27, 169, 218];
                        data.cell.styles.textColor = [255, 255, 255];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        });

        // --- Page 2: Title Page ---
        doc.addPage();
        const pageWidth2 = doc.internal.pageSize.getWidth();

        // 1. Report Title (Top Center) - Green
        doc.setFontSize(16);
        doc.setTextColor(0, 128, 0); // Green
        doc.setFont('helvetica', 'bold');
        doc.text("Gap Assessments Report", pageWidth2 / 2, 60, { align: "center" });

        // 2. Framework Title (Middle Center) - Blue
        doc.setFontSize(24);
        doc.setTextColor(0, 102, 255); // Blue
        doc.text("Smart Map Controls", pageWidth2 / 2, 130, { align: "center" });

        // 3. Framework Logo
        try {
            const titleLogo = new Image();
            titleLogo.src = '/img/CARA_DarkTheme.png';
            // Center the image: x = (pageWidth - imgWidth) / 2
            const imgW = 100;
            const imgH = 50; // Aspect ratio estimate
            doc.addImage(titleLogo, 'PNG', (pageWidth2 - imgW) / 2, 160, imgW, imgH);
        } catch (e) {
            console.warn("Title page logo error:", e);
        }

        // 4. Footer (Bottom Center) - Green
        doc.setFontSize(14);
        doc.setTextColor(0, 128, 0);
        doc.text("CARA PLATFORM", pageWidth2 / 2, 270, { align: "center" });

        // Reset for content
        doc.setTextColor(0, 0, 0);

        // --- Page 3+: Content ---

        // --- Page 2: Disclaimer Page ---
        doc.addPage();
        const marginLeft = 22;
        const marginRight = 32;
        const maxWidth = pageWidth - marginLeft - marginRight;
        let y = 40;

        // Header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text('DISCLAIMER OF WARRANTIES AND LIMITATION OF LIABILITIES', marginLeft, y);
        y += 16;

        // Sub header lines
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text('Copyright 2018-23 CyberInsurify Labs.', marginLeft, y);
        y += 7;
        doc.text('CyberInsurify.com is a product of CyberInsurify Labs.', marginLeft, y);
        y += 12;

        // Logo left aligned and smaller
        try {
            const caraLogo = new Image();
            caraLogo.src = '/img/cyberinsurify_logo.png';
            doc.addImage(caraLogo, 'PNG', marginLeft, y, 60, 22);
        } catch { }
        y += 30;

        // NO WARRANTY bold para
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10.5);
        const p1 = 'NO WARRANTY: THIS CYBERINSURIFY LABS MATERIAL IS FURNISHED ON AN AS-IS BASIS. CYBERINSURIFY LABS MAKES NO WARRANTIES OF ANY KIND, EITHER EXPRESSED OR IMPLIED, AS TO ANY MATTER INCLUDING, BUT NOT LIMITED TO, WARRANTY OF FITNESS FOR PURPOSE OR MERCHANTABILITY, EXCLUSIVITY, OR RESULTS OBTAINED FROM USE OF THE MATERIAL. CYBERINSURIFY LABS DOES NOT MAKE ANY WARRANTY OF ANY KIND WITH RESPECT TO FREEDOM FROM PATENT, TRADEMARK, OR COPYRIGHT INFRINGEMENT';
        const p1Lines = doc.splitTextToSize(p1, maxWidth);
        doc.text(p1Lines, marginLeft, y);
        y += p1Lines.length * 5 + 4;

        // Internal use
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10.5);
        const p2 = 'Internal use: Permission to reproduce this material and to prepare derivative works from this material for use inside your organization is granted, provided the copyright and No Warranty statements are included with all reproductions and derivative works.';
        const p2Lines = doc.splitTextToSize(p2, maxWidth);
        doc.text(p2Lines, marginLeft, y);
        y += p2Lines.length * 5 + 4;

        // External use
        const p3 = 'External use: This material may be reproduced in its entirety, without modification, and freely distributed in written or electronic form without requesting formal permission. Permission is required for any other external and/or commercial use. Requests for such permission should be directed to info@cyberinsurify.com.';
        const p3Lines = doc.splitTextToSize(p3, maxWidth);
        doc.text(p3Lines, marginLeft, y);
        y += p3Lines.length * 5 + 4;

        // PREPARED line
        doc.text('CyberInsurify Labs. PREPARED THIS REPORT.', marginLeft, y);
        y += 12;

        // NOTE label
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('NOTE', marginLeft, y);
        y += 10;

        // Footer
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10.5);
        const p4 = 'For further information about CyberInsurify Labs, email us info@cyberinsurify.com. Copyright 2018-23 CyberInsurify Labs. All rights reserved.';
        const p4Lines = doc.splitTextToSize(p4, maxWidth);
        doc.text(p4Lines, marginLeft, y);



        // --- Page 3: About CyberInsurify ---
        doc.addPage();
        doc.setPage(doc.internal.getNumberOfPages());
        const aboutMarginLeft = 22;
        const aboutMarginRight = 38;
        const aboutMaxWidth = pageWidth - aboutMarginLeft - aboutMarginRight;

        // Left-aligned CARA logo
        try {
            const aboutLogo = new Image();
            aboutLogo.src = '/img/cyberinsurify_logo.png';
            doc.addImage(aboutLogo, 'PNG', aboutMarginLeft, 30, 62, 22);
        } catch { }

        // Title: About CyberInsurify
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(32);
        doc.setTextColor(0, 0, 0);
        doc.text('About CyberInsurify', aboutMarginLeft, 82);

        let aboutY = 98; // Increased from 85 to prevent overlap with 32pt title
        const paragraphFontSize = 11;
        const paragraphLineHeight = 5; // Reduced from 5.3
        const paragraphSpacing = 5; // Reduced from 6
        const paragraphCharSpace = 0.3;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(paragraphFontSize);
        doc.setTextColor(0, 0, 0);

        const renderParagraph = (lines, startY, extraSpacing = paragraphSpacing) => {
            let yPos = startY;
            lines.forEach((line) => {
                doc.text(line, aboutMarginLeft, yPos, { charSpace: paragraphCharSpace });
                yPos += paragraphLineHeight;
            });
            return yPos + extraSpacing;
        };

        const aboutP1 = 'When we hear about hacking events targeting corporate, government and even small business, we are grateful our organization is not the victim. Potential victims often possess the perception that hackers will only sensitive targets such as large corporations or banks. While some use their skills & talent for ethical purposes, others threaten, steal and cause harm. The size of an organization is irrelevant to an intruder; and you become an immediate target if you catch attention of malicious entities.';
        const aboutP1Lines = doc.splitTextToSize(aboutP1, aboutMaxWidth);
        aboutY = renderParagraph(aboutP1Lines, aboutY);

        const aboutP2 = 'CyberInsurify is an Integrated Risk Management and Compliance Solutions provider. CARA platform is a web-based user-friendly Integrated Risk management system designed to manage Cybersecurity and Cyber Risk posture. Our intuitive and innovative software is constantly evolving to meet customer and industry needs. We provide you and your business with the technology to implement, adapt and scale quickly, improve operational efficiencies, and exceed your customers expectations.';
        const aboutP2Lines = doc.splitTextToSize(aboutP2, aboutMaxWidth);
        aboutY = renderParagraph(aboutP2Lines, aboutY, 6);

        doc.setFontSize(12);
        doc.text('We are solving the', aboutMarginLeft, aboutY);
        const prefixWidth = doc.getTextWidth('We are solving the ');
        doc.setTextColor(0, 138, 255);
        doc.text('One of the biggest security questions companies face is,', aboutMarginLeft + prefixWidth, aboutY, { charSpace: paragraphCharSpace });
        aboutY += paragraphLineHeight + 4;

        doc.setTextColor(210, 0, 0);
        doc.text('What is my business Cyber Risk Exposure and the financial impact of this Cyber Risk?', aboutMarginLeft, aboutY, { charSpace: paragraphCharSpace });
        aboutY += paragraphLineHeight + 6;

        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        doc.text('Gap Assessment Report Details', pageWidth / 2, aboutY, { align: 'center' });
        aboutY += 10;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(paragraphFontSize);
        const detailsP = 'This report represents the results of an evaluation using the National Institute of Standards and Technology Cybersecurity Framework (NIST CSF). The Framework provides a common organizing structure for multiple approaches to cybersecurity by assembling standards, guidelines, and practices that are working effectively today.';
        const detailsLines = doc.splitTextToSize(detailsP, aboutMaxWidth);
        aboutY = renderParagraph(detailsLines, aboutY);

        const created = new Date();
        const createdStr = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}-${String(created.getDate()).padStart(2, '0')} ${String(created.getHours()).padStart(2, '0')}:${String(created.getMinutes()).padStart(2, '0')}:${String(created.getSeconds()).padStart(2, '0')}`;


        const metaLineGap = 6; // Reduced from 8

        doc.text('This report was created on : ', aboutMarginLeft, aboutY);
        doc.setFont('helvetica', 'bold');
        doc.text(createdStr, aboutMarginLeft + doc.getTextWidth('This report was created on : '), aboutY);
        aboutY += metaLineGap;

        doc.setFont('helvetica', 'normal');
        doc.text('The owner of this assessment is : ', aboutMarginLeft, aboutY);
        doc.setFont('helvetica', 'bold');
        doc.text(ownerEmail, aboutMarginLeft + doc.getTextWidth('The owner of this assessment is : '), aboutY);
        aboutY += metaLineGap;

        doc.setFont('helvetica', 'normal');
        doc.text('The scope defined for this assessment is : ', aboutMarginLeft, aboutY);
        doc.setFont('helvetica', 'bold');
        doc.text(scopeText, aboutMarginLeft + doc.getTextWidth('The scope defined for this assessment is : '), aboutY);

        aboutY += metaLineGap + 2;


        doc.setFont('helvetica', 'normal');
        doc.setFontSize(paragraphFontSize);
        const closing = 'The results presented in this report are based on customer responses. For the purposes of this assessment, responses are considered valid and accurate. The process did not include document reviews, observations of work, or an examination of security controls in place to support the evaluated scope.';
        const closingLines = doc.splitTextToSize(closing, aboutMaxWidth);
        closingLines.forEach((line) => {
            doc.text(line, aboutMarginLeft, aboutY, { charSpace: paragraphCharSpace });
            aboutY += paragraphLineHeight;
        });

        // --- Page 4: Table of Contents ---
        // --- Page 4: Table of Contents ---
        doc.addPage();
        tocPageNumber = doc.internal.getNumberOfPages();
        doc.setPage(tocPageNumber);
        const tocMarginLeft = 22; // Reduced from 44
        const tocMarginRight = 22; // Reduced from 44
        const pageHeight = doc.internal.pageSize.getHeight();

        // Header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text('Table of Contents', tocMarginLeft, 30);

        const drawTocLine = (item) => {
            // Check if we need a new page
            if (tocY > pageHeight - 30) {
                doc.addPage();
                tocY = 28; // Start from top of new page
            }

            const {
                label,
                page,
                bold = false,
                fontSize = bold ? 12 : 11,
                indent = 0,
                dotGap = 4.2,
            } = item;

            const labelX = tocMarginLeft + indent;
            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setFontSize(fontSize);
            doc.text(label, labelX, tocY);

            const pageText = String(page);
            const pageTextWidth = doc.getTextWidth(pageText);
            const pageX = pageWidth - tocMarginRight - pageTextWidth;

            const labelWidth = doc.getTextWidth(label);
            const dotsStart = labelX + labelWidth + 4;
            const dotsEnd = pageX - 2;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9.5);
            for (let dotX = dotsStart; dotX < dotsEnd; dotX += dotGap) {
                doc.text('.', dotX, tocY);
            }

            doc.setFont('helvetica', bold ? 'bold' : 'normal');
            doc.setFontSize(fontSize);
            doc.text(pageText, pageX, tocY);

            tocY += item.spacing;
        };

        let tocY = 40; // Reduced from 60 to start higher

        const tocItems = (() => {
            const items = [];

            sectionOrder.forEach((sectionKey) => {
                const entries = sections[sectionKey] || [];
                if (!entries.length) return;

                items.push({
                    label: sectionConfig[sectionKey]?.title || sectionKey,
                    page: '',
                    spacing: 6.5,
                    bold: true,
                    sectionKey,
                });

                entries.forEach((entry, index) => {
                    items.push({
                        label: entry.name || `${sectionKey} ${index + 1}`,
                        page: '',
                        spacing: 5.2,
                        indent: 14,
                        sectionKey,
                        entryIndex: index,
                    });
                });

                if (items.length > 0) {
                    items[items.length - 1].spacing = 7; // Reduced from 10
                }
            });

            return items;
        })();

        const sectionPageMap = {};
        const entryPageMap = {};
        const getEntryKey = (sectionKey, index) => `${sectionKey}_${index}`;
        const recordSectionPage = (sectionKey, pageNumber) => {
            if (!sectionKey || sectionPageMap[sectionKey]) return;
            sectionPageMap[sectionKey] = pageNumber;
        };
        const recordEntryPage = (sectionKey, index, pageNumber) => {
            const key = getEntryKey(sectionKey, index);
            if (!entryPageMap[key]) {
                entryPageMap[key] = pageNumber;
            }
        };

        const addSectionIntro = (sectionKey, titleText, descriptionText) => {
            const margin = 22;
            const pageHeight = doc.internal.pageSize.getHeight();
            const pageWidth = doc.internal.pageSize.getWidth();
            const usableWidth = pageWidth - margin * 2;

            doc.addPage();
            recordSectionPage(sectionKey, doc.getNumberOfPages());

            let cursorY = 24;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            doc.text(titleText, margin, cursorY);
            cursorY += 12;

            if (descriptionText) {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                const lines = doc.splitTextToSize(descriptionText, usableWidth);
                lines.forEach((line) => {
                    if (cursorY > pageHeight - margin) {
                        doc.addPage();
                        recordSectionPage(sectionKey, doc.getNumberOfPages());
                        cursorY = 24;
                    }
                    doc.text(line, margin, cursorY);
                    cursorY += 5.2;
                });
                cursorY += 4;
            }

            return {
                margin,
                cursorY,
                usableWidth,
                pageHeight,
            };
        };

        const renderSectionTables = (sectionKey) => {
            const entries = sections[sectionKey] || [];
            if (!entries.length) return;

            const config = sectionConfig[sectionKey] || {};
            const title = config.title || sectionKey;
            const description = contentMap?.[sectionKey]?.description || config.description || '';

            const intro = addSectionIntro(sectionKey, title, description);
            if (!intro) return;

            const { margin, usableWidth, pageHeight } = intro;
            let cursorY = intro.cursorY;

            const defaultBodyStyles = {
                font: 'helvetica',
                fontSize: 9,
                cellPadding: { top: 1.6, bottom: 1.6, left: 3, right: 2 },
                textColor: [0, 0, 0],
                lineColor: [120, 120, 120],
                lineWidth: 0.2,
            };

            const baseTableOptions = {
                styles: defaultBodyStyles,
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    cellPadding: 2,
                },
                margin: { left: margin, right: margin },
                tableLineColor: [120, 120, 120],
                tableLineWidth: 0.2,
            };

            const applyCellStyling = (data) => {
                if (data.section !== 'body') return;

                if (data.column.index === 0) {
                    data.cell.styles.fillColor = config.firstColumnFill || [230, 230, 230];
                    data.cell.styles.textColor = config.firstColumnText || [0, 0, 0];
                    data.cell.styles.fontStyle = 'bold';
                }

                if (data.column.index === 2) {
                    data.cell.styles.fillColor = config.priorityFill || [224, 224, 224];
                    data.cell.styles.textColor = config.priorityText || [0, 0, 0];
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.halign = 'center';
                }
            };

            entries.forEach((entry, index) => {
                const entryTitle = entry.name || `${title} ${index + 1}`;
                const entryHeading = entry.heading || headingLookup[buildHeadingKey(sectionKey, entry.categoryCode)] || '';
                const entryDescription = entry.description || contentMap?.[sectionKey]?.subtext || '';
                const normalizedHeading = entryHeading?.trim?.() || '';
                const normalizedDescription = entryDescription?.trim?.() || '';
                const shouldRenderDescription =
                    normalizedDescription && normalizedDescription !== normalizedHeading;
                const entryRows = Array.isArray(entry.rows) && entry.rows.length
                    ? entry.rows
                    : Array.isArray(entry.source?.subcategory?.questions)
                        ? entry.source.subcategory.questions.map((q) => ({
                            categoryId: q.category_id || q.categoryid || '',
                            question: q.question || '',
                            priority: q.priority || 'N/A',
                            status: formatStatusValue(q.status || q.result || 'N/A'),
                            remark: q.notes || q.remark || '',
                        }))
                        : [];

                if (cursorY > pageHeight - 40) {
                    doc.addPage();
                    recordSectionPage(sectionKey, doc.getNumberOfPages());
                    cursorY = 24;
                    doc.text(`${title} (cont.)`, margin, cursorY);
                    cursorY += 12;
                }

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                recordEntryPage(sectionKey, index, doc.getNumberOfPages());
                doc.text(entryTitle, margin, cursorY);
                cursorY += 6;

                if (normalizedHeading) {
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(10);
                    const headingLines = doc.splitTextToSize(entryHeading, usableWidth);
                    headingLines.forEach((line) => {
                        if (cursorY > pageHeight - margin) {
                            doc.addPage();
                            recordSectionPage(sectionKey, doc.getNumberOfPages());
                            cursorY = 24;
                            doc.setFont('helvetica', 'bold');
                            doc.setFontSize(11);
                            doc.text(`${entryTitle} (cont.)`, margin, cursorY);
                            cursorY += 6;
                            doc.setFont('helvetica', 'normal');
                            doc.setFontSize(10);
                        }
                        doc.text(line, margin, cursorY);
                        cursorY += 5.2;
                    });
                    cursorY += 4;
                }

                if (shouldRenderDescription) {
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(10);
                    const lines = doc.splitTextToSize(entryDescription, usableWidth);
                    lines.forEach((line) => {
                        if (cursorY > pageHeight - margin) {
                            doc.addPage();
                            recordSectionPage(sectionKey, doc.getNumberOfPages());
                            cursorY = 24;
                            doc.setFont('helvetica', 'bold');
                            doc.setFontSize(11);
                            doc.text(`${entryTitle} (cont.)`, margin, cursorY);
                            cursorY += 6;
                            doc.setFont('helvetica', 'normal');
                            doc.setFontSize(10);
                        }
                        doc.text(line, margin, cursorY);
                        cursorY += 5.2;
                    });
                    cursorY += 4;
                }

                const tableBody = entryRows.map((row) => {
                    const descriptionParts = [row.question || ''];
                    if (row.remark) descriptionParts.push(`Remark: ${row.remark}`);

                    const statusParts = [];
                    const formattedStatus = formatStatusValue(row.status);

                    if (formattedStatus) statusParts.push(formattedStatus);

                    return [
                        row.categoryId || 'N/A',
                        descriptionParts.join('\n').trim(),
                        statusParts.join('\n') || 'N/A',
                    ];
                });

                autoTable(doc, {
                    startY: cursorY,
                    head: [['Function ID', 'Description', 'Current']],
                    body: tableBody,
                    columnStyles: {
                        0: { cellWidth: 52 },
                        1: { cellWidth: usableWidth - 92 },
                        2: { cellWidth: 40 },
                    },
                    alternateRowStyles: { fillColor: config.alternateFill || [240, 240, 240] },
                    didParseCell: applyCellStyling,
                    ...baseTableOptions,
                });

                cursorY = doc.lastAutoTable.finalY + 12;
            });
        };

        sectionOrder.forEach(renderSectionTables);

        // Go back to TOC Page and fill in page numbers
        console.log('Forcing TOC Items to Page: ' + tocPageNumber);
        doc.setPage(tocPageNumber);
        tocY = 40; // Reduced to match initial position
        tocItems.forEach((item) => {
            let pageNumber;
            if (item.sectionKey) {
                if (typeof item.entryIndex === 'number') {
                    pageNumber = entryPageMap[getEntryKey(item.sectionKey, item.entryIndex)];
                }
                if (!pageNumber) {
                    pageNumber = sectionPageMap[item.sectionKey];
                }
            } else {
                pageNumber = item.page;
            }

            const pageValue = pageNumber ? String(pageNumber) : '';
            drawTocLine({ ...item, page: pageValue });
        });
        doc.setPage(doc.getNumberOfPages());

        doc.save(customFileName || "Controls_List.pdf");
        if (setShowControlsDropdown) setShowControlsDropdown(false);
        alert(i18n.t('msg.controlsPdfDownloadedSuccess', { ns: 'framework_common', defaultValue: 'Controls PDF file downloaded successfully!' }));
    } else {
        alert(i18n.t('msg.noControlDataAvailableForExport', { ns: 'framework_common', defaultValue: ' No control data available for export.' }));
        if (setShowControlsDropdown) setShowControlsDropdown(false);
    }
}